import React, { Component } from 'react';
import { getMusicEngineHomeData, getUnifiedCurrentUser } from '../../api/api';
import PageSpinner from 'components/Spinner/PageSpinner';
import PodcastList from 'components/PodcastList/PodcastList';
import Header from 'components/Header/Header';
import Navigation from 'navigation/navigation';
import Style from './Podcast.module.css';
import { logEventPagesViewed } from 'utils/analytics';

class Podcast extends Component {

    constructor(props) {
        super(props);
        this.state = {
            user: null,
            podcastFeed: [],
            loading: true,
            successMessage: '',
            errMessage: '',
            // MusicEngine Homepage Data
            channels: [], // MusicEngine channels with podcasts
            slides: [], // MusicEngine slides
            // Original data for filtering
            originalChannels: [],
            originalSlides: [],
            // Topic filtering
            selectedTopics: ['all'],
            filteredChannels: []
        };
    }

    async componentDidMount() {
        // Track page view (non-blocking)
        logEventPagesViewed(null, window.location.href, 'podcast_page', 'Podcast').catch(() => {});
        
        // Load selected topics from localStorage or props
        this.loadSelectedTopics();
        
        this.loadOfflineData();
        await this.getUser();
    }

    componentDidUpdate(prevProps) {
        // Check if selected topics changed from navigation
        if (this.props.location.state?.selectedTopics !== prevProps.location.state?.selectedTopics) {
            this.loadSelectedTopics();
        }
    }

    loadSelectedTopics = () => {
        const { selectedTopics } = this.props.location.state || {};
        const storedTopics = localStorage.getItem('selectedTopics');
        
        let topics = ['all']; // Default to show all topics
        
        if (selectedTopics) {
            topics = selectedTopics;
        } else if (storedTopics) {
            try {
                topics = JSON.parse(storedTopics);
            } catch (error) {
                topics = ['all'];
            }
        }
        
        this.setState({ selectedTopics: topics }, () => {
            // Apply topic filtering if channels are already loaded
            if (this.state.originalChannels.length > 0) {
                this.filterChannelsByTopics();
            }
        });
    };

    getUser = async () => {
        try {
            const { user } = await getUnifiedCurrentUser();
            if (user) {
                this.setState({ user: user });
            }
            await this.loadMusicEngineHomeData();
        } catch (error) {
            await this.loadKaiOSData();
        }
    };

    loadMusicEngineHomeData = async () => {
        try {
            const homeData = await getMusicEngineHomeData();

            const transformedData = this.transformMusicEngineHomeData(homeData);

            const originalData = {
                originalChannels: transformedData.channels || [],
                originalSlides: transformedData.slides || []
            };

            this.setState({
                ...transformedData,
                ...originalData,
                loading: false,
                errMessage: ''
            }, () => {
                // Apply initial topic filtering
                this.filterChannelsByTopics();
            });
        } catch (error) {
            await this.loadKaiOSData();
        }
    };

    transformMusicEngineHomeData = (homeData) => {
        const transformed = {};
        if (homeData && homeData.slides) {
            transformed.slides = this.createMusicEngineSlides(homeData.slides);
        }
        if (homeData && homeData.channels) {
            transformed.channels = this.createMusicEngineChannels(homeData.channels);
        }
        return transformed;
    };

    createMusicEngineSlides = (slides) => {
        return slides.map((slide, index) => {
            return {
                id: slide.id || index,
                title: slide.title || 'Featured Content',
                description: slide.description || '',
                image: slide.artwork_url || slide.image || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiB2aWV3Qm94PSIwIDAgMTIwIDgwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjNDUxZTUzIi8+Cjx0ZXh0IHg9IjYwIiB5PSI0MCIgZm9udC1tYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEwIiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGNlIiBkeT0iLjNlbSI+RmFpbGVkPC90ZXh0Pgo8L3N2Zz4=',
                objectType: slide.object_type || 'podcast',
                object: slide.object || null
            };
        });
    };

    createMusicEngineChannels = (channels) => {
        return channels.map((channel, index) => {
            let podcasts = [];
            if (channel.objects && channel.objects.data) {
                podcasts = channel.objects.data.map(podcast => ({
                    id: podcast.id || Math.random().toString(36).substr(2, 9),
                    title: podcast.title || podcast.name || 'Podcast',
                    author: podcast.artist ? podcast.artist.name : (podcast.author || podcast.creator || 'Unknown'),
                    description: podcast.description || podcast.bio || '',
                    image: podcast.artwork_url || podcast.image || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiB2aWV3Qm94PSIwIDAgMTIwIDgwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjNDUxZTUzIi8+Cjx0ZXh0IHg9IjYwIiB5PSI0MCIgZm9udC1tYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEwIiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGNlIiBkeT0iLjNlbSI+UG9kY2FzdDwvdGV4dD4KPC9zdmc+',
                    rssURL: podcast.rss_feed_url || podcast.rss_url || podcast.feed_url || '',
                    episodeCount: podcast.episodes_count || podcast.episode_count || 0,
                    language: podcast.language || 'English'
                }));
            }
            const transformedPodcasts = podcasts.map(podcast => ({
                id: podcast.id || podcast._id || Math.random().toString(36).substr(2, 9),
                title: podcast.title || podcast.name || 'Podcast',
                author: podcast.artist ? podcast.artist.name : (podcast.author || podcast.creator || podcast.artist_name || 'Unknown'),
                description: podcast.description || podcast.bio || podcast.summary || '',
                image: podcast.artwork_url || podcast.image || podcast.artwork || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiB2aWV3Qm94PSIwIDAgMTIwIDgwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjNDUxZTUzIi8+Cjx0ZXh0IHg9IjYwIiB5PSI0MCIgZm9udC1tYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEwIiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGNlIiBkeT0iLjNlbSI+RmFpbGVkPC90ZXh0Pgo8L3N2Zz4=',
                rssURL: podcast.rss_feed_url || podcast.rss_url || podcast.feed_url || podcast.rss || '',
                episodeCount: podcast.episodes_count || podcast.episode_count || podcast.episodes || 0,
                language: podcast.language?.name || podcast.language || 'English'
            }));
            return {
                id: channel.id || index,
                title: channel.title || channel.name || 'Channel',
                description: channel.description || channel.bio || '',
                count: transformedPodcasts.length,
                image: channel.artwork_url || channel.image || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiB2aWV3Qm94PSIwIDAgMTIwIDgwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjNDUxZTUzIi8+Cjx0ZXh0IHg9IjYwIiB5PSI0MCIgZm9udC1tYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEwIiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGNlIiBkeT0iLjNlbSI+Q2hhbm5lbDwvdGV4dD4KPC9zdmc+',
                slug: channel.alt_name || channel.slug || channel.title?.toLowerCase().replace(/\s+/g, '-') || 'channel',
                podcasts: transformedPodcasts, // Include the transformed podcasts for this channel
                objectType: channel.object_type || 'podcast'
            };
        });
    };

    // Topic filtering logic
    filterChannelsByTopics = () => {
        const { originalChannels, selectedTopics } = this.state;
        
        // If no topics selected: show only Amplifying section (handled in render) and no additional channels
        if (!selectedTopics || selectedTopics.length === 0) {
            this.setState({ filteredChannels: [] });
            return;
        }

        // Filter channels based on selected topics (match by slug/alt_name/title normalized)
        const normalizedSelected = selectedTopics.map(t => String(t).toLowerCase());
        const filteredChannels = originalChannels.filter(channel => {
            const slug = (channel.slug || channel.id || '').toString().toLowerCase();
            const alt = (channel.alt_name || '').toString().toLowerCase();
            const title = (channel.title || '').toString().toLowerCase().replace(/\s+/g, '-');
            return normalizedSelected.some(sel => sel === slug || sel === alt || sel === title);
        });

        this.setState({ filteredChannels });
    };

    loadKaiOSData = async () => {
        try {
            // Fallback to KaiOS data if MusicEngine fails
            this.setState({
                loading: false,
                errMessage: 'MusicEngine unavailable, showing offline content'
            });
        } catch (error) {
            this.setState({
                loading: false,
                errMessage: 'Failed to load content'
            });
        }
    };

    loadOfflineData = () => {
        const topThreePodcastsData = localStorage.getItem('topThreePodcastsData');
        const podcastFeeds = topThreePodcastsData ? JSON.parse(topThreePodcastsData) : null;
        if (podcastFeeds) {
            this.setState({
                podcastFeed: podcastFeeds,
                errMessage: ''
            });
        }
    };

    onClickViewAllTopTen = async () => {
        this.props.history.push('/home/topTenPodcasts');
    };

    renderFeaturedSlides = () => {
        const { slides } = this.state;
        if (!slides || slides.length === 0) return null;

        return (
            <div className={Style.featuredSection}>
                <div className={Style.divTitleSection}>
                    <div className={Style.divTitleRow}>Featured</div>
                </div>
                <div className={Style.slidesContainer}>
                    {slides.slice(0, 5).map((slide, index) => (
                        <div 
                            key={slide.id || index} 
                            className={Style.slideItem}
                            onClick={() => this.handleItemClick(slide, 'featured')}
                        >
                            <img 
                                src={slide.image} 
                                alt={slide.title} 
                                className={Style.slideImage}
                                onError={(e) => {
                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiB2aWV3Qm94PSIwIDAgMTIwIDgwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjNDUxZTUzIi8+Cjx0ZXh0IHg9IjYwIiB5PSI0MCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEwIiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGNlIiBkeT0iLjNlbSI+RmFpbGVkPC90ZXh0Pgo8L3N2Zz4=';
                                }}
                            />
                            <div className={Style.slideTitle}>{slide.title}</div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    renderAmplifyingSection = () => {
        const { originalChannels, filteredChannels } = this.state;
        const candidates = [...(originalChannels || []), ...(filteredChannels || [])];
        const amplifyingChannel = candidates.find(ch =>
            (ch && ch.title && /amplifying|podcasthers|her voice/i.test(ch.title))
        ) || (originalChannels && originalChannels.length ? originalChannels[0] : null);

        const podcasts = (amplifyingChannel && amplifyingChannel.podcasts) ? amplifyingChannel.podcasts.slice(0, 5) : [];

        return (
            <div className={Style.contentSection}>
                <div className={Style.divTitleSection}>
                    <div className={Style.divTitleRow}>PodcastHers: Amplifying Her Voice</div>
                    <div className={Style.divSubtitleRow}>Women empowerment and leadership content</div>
                </div>
                <div className={Style.itemsContainer}>
                    {podcasts.length > 0 ? podcasts.map((podcast, idx) => (
                        <div
                            key={podcast.id || podcast._id || idx}
                            className={Style.itemCard}
                            onClick={() => this.handleItemClick(podcast, 'podcast')}
                        >
                            <img
                                src={podcast.image}
                                alt={podcast.title}
                                className={Style.itemImage}
                                onError={(e) => {
                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiB2aWV3Qm94PSIwIDAgMTIwIDgwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjNDUxZTUzIi8+Cjx0ZXh0IHg9IjYwIiB5PSI0MCIgZm9udC1tYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEwIiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGNlIiBkeT0iLjNlbSI+RmFpbGVkPC90ZXh0Pgo8L3N2Zz4=';
                                }}
                            />
                            <div className={Style.itemContent}>
                                <div className={Style.itemName}>{podcast.title}</div>
                                <div className={Style.itemAuthor}>by {podcast.author}</div>
                            </div>
                        </div>
                    )) : (
                        // Fallback placeholders when no channel/podcasts found
                        [0,1,2,3,4].map((idx) => (
                            <div key={idx} className={Style.itemCard}>
                                <img
                                    src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiB2aWV3Qm94PSIwIDAgMTIwIDgwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjRkY2OUI5Ii8+Cjx0ZXh0IHg9IjYwIiB5PSI0MCIgZm9udC1tYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEwIiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGNlIiBkeT0iLjNlbSI+V29tZW48L3RleHQ+Cjwvc3ZnPg=="
                                    alt="Women Empowerment"
                                    className={Style.itemImage}
                                />
                                <div className={Style.itemContent}>
                                    <div className={Style.itemName}>Women Empowerment</div>
                                    <div className={Style.itemAuthor}>by Sheqonomi</div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
                {amplifyingChannel && amplifyingChannel.podcasts && amplifyingChannel.podcasts.length > 5 && (
                    <div style={{ height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '10px', marginBottom: '20px' }}>
                        <span className={Style.button} style={{ cursor: 'pointer' }} onClick={() => this.handleViewAllClick(amplifyingChannel.title)}>
                            See All ({amplifyingChannel.podcasts.length})
                        </span>
                    </div>
                )}
            </div>
        );
    };

    renderMusicEngineChannels = () => {
        const { filteredChannels } = this.state;
        
        if (!filteredChannels || filteredChannels.length === 0) {
            return (
                <div className={Style.contentSection}>
                    <div className={Style.divTitleSection}>
                        <div className={Style.divTitleRow}>No Content Found</div>
                        <div className={Style.divSubtitleRow}>Try selecting different topics or showing all topics</div>
                    </div>
                </div>
            );
        }

        return filteredChannels.map((channel, index) => {
            
            return (
                <div key={channel.id || index} className={Style.contentSection}>
                    <div className={Style.divTitleSection}>
                        <div className={Style.divTitleRow}>{channel.title}</div>
                        {channel.description && (
                            <div className={Style.divSubtitleRow}>{channel.description}</div>
                        )}
                    </div>
                    <div className={Style.itemsContainer}>
                        {channel.podcasts && channel.podcasts.slice(0, 5).map((podcast, podcastIndex) => (
                            <div
                                key={podcast.id || podcastIndex}
                                className={Style.itemCard}
                                onClick={() => this.handleItemClick(podcast, 'podcast')}
                            >
                                <img
                                    src={podcast.image}
                                    alt={podcast.title}
                                    className={Style.itemImage}
                                    onError={(e) => {
                                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiB2aWV3Qm94PSIwIDAgMTIwIDgwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjNDUxZTUzIi8+Cjx0ZXh0IHg9IjYwIiB5PSI0MCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEwIiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGNlIiBkeT0iLjNlbSI+RmFpbGVkPC90ZXh0Pgo8L3N2Zz4=';
                                    }}
                                />
                                <div className={Style.itemContent}>
                                    <div className={Style.itemName}>{podcast.title}</div>
                                    <div className={Style.itemAuthor}>by {podcast.author}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                    {channel.podcasts && channel.podcasts.length > 5 && (
                        <div style={{ height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '10px', marginBottom: '20px' }}>
                            <span className={Style.button} style={{ cursor: 'pointer' }} onClick={() => this.handleViewAllClick(channel.title)}>
                                See All ({channel.podcasts.length})
                            </span>
                        </div>
                    )}
                </div>
            );
        });
    };

    handleItemClick = (item, type) => {
        // Prefer robust ID detection across sources
        const podcastId = (item && (item.id || item._id || item.podcastId || item.podcast_id));
        if (podcastId) {
            this.props.history.push(`/podcastdetail/${podcastId}`);
            return;
        }
    };

    handleViewAllClick = (sectionTitle) => {
        // Find the channel by title to pass full list to SeeAll page
        const channel = (this.state.originalChannels || []).find(ch => ch.title === sectionTitle);
        const podcasts = (channel && Array.isArray(channel.podcasts)) ? channel.podcasts : [];
        this.props.history.push({
            pathname: '/podcastlist',
            state: { title: sectionTitle, podcasts }
        });
    };

    render() {
        const { podcastFeed, loading, successMessage, errMessage, selectedTopics } = this.state;
        return (
            <div className={Style.container}>
                <Header title='Podcast' showTopicSelector={true} />
                {loading ? (
                    <PageSpinner />
                ) : (
                            <React.Fragment>
                                {successMessage.length ? <span style={{ marginTop: 10, color: 'green', fontSize: '0.8em' }}>{successMessage}</span> : null}
                                {errMessage.length ? <span style={{ marginTop: 10, color: 'red', fontSize: '0.8em' }}>{errMessage}</span> : null}
                        
                        {/* PodcastHers: Amplifying Her Voice - Always Visible, clickable cards */}
                        {this.renderAmplifyingSection()}

                        {/* Topic Selection Info removed per requirement */}
                        
                        {/* Featured Slides Section */}
                        {this.renderFeaturedSlides()}

                        {/* MusicEngine Channels Section - Displaying filtered channels with podcasts only when topics selected */}
                        {Array.isArray(selectedTopics) && selectedTopics.length > 0 && this.renderMusicEngineChannels()}

                        {/* Fallback: Original Top 10 Section */}
                        {podcastFeed.length > 0 && (
                            <div className={Style.contentSection}>
                                <div className={Style.divTitleSection}>
                                    <div className={Style.divTitleRow}>Top 10</div>
                                </div>
                                <PodcastList data={podcastFeed} loading={loading} activeElement={0} />
                                <div style={{ height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <span className={Style.button} style={{ backgroundColor: '#fff', cursor: 'pointer', marginRight: '5px' }} onClick={this.onClickViewAllTopTen}>
                                        View All
                                    </span>
                                </div>
                            </div>
                        )}

                                <div style={{ margin: '5px', border: '1px solid #eae7e7' }}></div>
                                <div style={{ height: '100px' }}></div>
                        <Navigation active='Podcast' />
                    </React.Fragment>
                )}
            </div>
        );
    }
}

export default Podcast;



